import { useEffect, useRef, useState } from 'react';
import { getAlarmRuleErrorMessage } from './filterParameterMapping';

export const useAlarmReport = ({ request, exportCsv, exportExcel, service, notifications, errorTitle, exportErrorTitle }) => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const mountedRef = useRef(false);
  const loadingRef = useRef(false);
  const exportingRef = useRef(false);
  const generationRef = useRef(0);
  const reportId = useRef(null);
  const [loaders] = useState(() => ({
    occurrences: params => service.getReportRunOccurrences(reportId.current, params),
    events: ({ occurrenceIndex, ...params }) => service.getReportRunOccurrenceEvents(reportId.current, occurrenceIndex, params),
  }));

  const runReport = async () => {
    if (!mountedRef.current || loadingRef.current) return;
    const generation = ++generationRef.current;
    loadingRef.current = true;
    reportId.current = null;
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const report = await request();
      if (!mountedRef.current || generation !== generationRef.current) return;
      reportId.current = report.reportRunId;
      setResult(report);
    } catch (requestError) {
      if (!mountedRef.current || generation !== generationRef.current) return;
      const message = getAlarmRuleErrorMessage(requestError, `${errorTitle}.`);
      setError(message);
      notifications.error(errorTitle, message);
    } finally {
      if (mountedRef.current && generation === generationRef.current) {
        loadingRef.current = false;
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    loadingRef.current = false;
    void runReport();
    return () => {
      mountedRef.current = false;
      generationRef.current += 1;
    };
  }, [request]);

  const exportReport = async exporter => {
    if (!mountedRef.current || loadingRef.current || exportingRef.current) return;
    exportingRef.current = true;
    setExporting(true);
    try {
      await exporter();
    } catch (exportError) {
      if (mountedRef.current) notifications.error(exportErrorTitle, getAlarmRuleErrorMessage(exportError, 'Please try again.'));
    } finally {
      exportingRef.current = false;
      if (mountedRef.current) setExporting(false);
    }
  };

  const cancel = () => {
    mountedRef.current = false;
    generationRef.current += 1;
  };

  return {
    result,
    loading,
    error,
    exporting,
    loaders,
    runReport,
    exportCsv: () => exportReport(exportCsv),
    exportExcel: () => exportReport(exportExcel),
    cancel,
  };
};